import { Link } from "react-router-dom";
import { CheckCircle2, Users, Heart, ShieldCheck, Target, Compass } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import SITE from "@/lib/site";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader
        eyebrow="About Ambuly"
        title="Redesigning emergency response, one life at a time."
        description="Ambuly was founded on a simple belief: no one should wonder whether help will arrive in time. We're building the infrastructure that turns life-or-death minutes into calm, coordinated care."
      />

      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#1A3A32]">Our story</p>
            <h2 className="mt-3 text-4xl sm:text-5xl font-[Outfit] font-semibold text-[#14241F]">
              Born out of a call that came too late.
            </h2>
            <div className="mt-6 space-y-5 text-[#495D56] text-lg leading-relaxed">
              <p>
                In 2023, one of our founders watched a family member wait 42 minutes for
                an ambulance during a stroke. That incident changed everything.
              </p>
              <p>
                Ambuly was built to replace the fragmented, phone-book-driven ambulance
                network with a single connected platform — real-time GPS, verified
                operators, trained crews and transparent pricing.
              </p>
              <p>
                We're headquartered in {SITE.legal.jurisdiction} and are building a
                network of hospitals, insurers, corporates, drivers and paramedics to
                make sure help is never more than minutes away.
              </p>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative">
              <div className="absolute -inset-3 bg-[#E5383B] rounded-3xl rotate-2 hidden sm:block" aria-hidden />
              <img
                src={SITE.media.professional}
                alt="Paramedic team at work"
                className="relative w-full h-[520px] object-cover rounded-3xl border border-[#D5DDD7] shadow-xl"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-[#EDF1ED]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#1A3A32]">What drives us</p>
            <h2 className="mt-3 text-4xl sm:text-5xl font-[Outfit] font-semibold text-[#14241F]">Our values</h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Heart, title: "Patients first", desc: "Every decision starts with: does this help the patient get better care, faster?" },
              { icon: ShieldCheck, title: "Uncompromising safety", desc: "Verified partners, audited equipment, trained crews. No shortcuts." },
              { icon: Target, title: "Radical transparency", desc: "Clear pricing, itemised bills, and an open line for feedback." },
              { icon: Users, title: "Built with caregivers", desc: "Paramedics and hospitals shape the product alongside our engineers." },
              { icon: Compass, title: "Always reachable", desc: "Designed for round-the-clock operations because emergencies don't wait." },
              { icon: CheckCircle2, title: "Accountable", desc: "We measure ETAs, outcomes and satisfaction — and publish our numbers." },
            ].map((v, i) => (
              <div key={v.title} data-testid={`value-card-${i}`} className="bg-white border border-[#D5DDD7] rounded-2xl p-8">
                <v.icon className="h-9 w-9 text-[#1A3A32]" />
                <h3 className="mt-5 text-xl font-[Outfit] font-semibold text-[#14241F]">{v.title}</h3>
                <p className="mt-2 text-[#495D56]">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-[#14241F] text-white p-10 sm:p-14">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <h3 className="text-3xl sm:text-4xl font-[Outfit] font-semibold">Partner with Ambuly.</h3>
                <p className="mt-3 text-[#D5DDD7]/80 text-lg max-w-2xl">
                  Operating an ambulance service, a hospital or an insurance plan?
                  Let's build faster, safer access to emergency care together.
                </p>
              </div>
              <div className="lg:col-span-4 flex justify-start lg:justify-end">
                <Link to="/contact" data-testid="about-partner-btn" className="inline-flex items-center justify-center bg-[#E5383B] hover:bg-[#BA1826] rounded-lg px-8 py-4 font-bold text-lg">
                  Talk to us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
